import PropTypes from 'prop-types';
import React from 'react';
import { withLocalize } from 'react-localize-redux';
import { isNullOrUndefined } from '../../../store/storeFunctions';
import NoDataComponent from "../../common/noDataComponent";

import './esg.scss';

const SEGMENTS = {
    n1: "n_1",
    n2: "n_2",
    n3: "n_3",
    n4: "n_4",
    n5: "n_5",
}

const SEGMENT_COLORS = {
    [SEGMENTS.n1]: {
        colorStart: '#02c59d',
        colorEnd: '#76c663'
    },
    [SEGMENTS.n2]: {
        colorStart: '#8fc851',
        colorEnd: '#c7cf2e'
    },
    [SEGMENTS.n3]: {
        colorStart: '#ead01a',
        colorEnd: '#f8ad1a'
    },
    [SEGMENTS.n4]: {
        colorStart: '#f7981d',
        colorEnd: '#f36d21'
    },
    [SEGMENTS.n5]: {
        colorStart: '#e64724',
        colorEnd: '#c92527'
    },
}

const INNER_CIRCLE_COLOR = "#f0f0f0";
const POINTER_COLOR = "#444749";

class ESGTachometer extends React.Component {
    render() {
        const { data, score } = this.props;
        return (isNullOrUndefined(data) || isNullOrUndefined(score) ? <NoDataComponent /> : this.renderContent(data, score));
    }

    renderContent(data, score) {
        const segmentData = Object.keys(SEGMENT_COLORS).map(k => ({ name: k, value: data[k], ...SEGMENT_COLORS[k] }));

        const radius = 32;
        const { segments, pointerAngle } = this.getSegments(segmentData, score, radius);
        const gradients = this.getGradients(segmentData);
        const innerRadius = radius * 0.66;
        const pointer = this.getPointer(pointerAngle, radius);

        return (
            <svg viewBox="0 0 80 70" className="esg-tachometer" aria-label="ESG Tachometer" preserveAspectRatio="xMidYMid meet">
                <defs>
                    {gradients}
                </defs>
                {segments}
                <circle cx="40" cy="40" r={innerRadius} fill={INNER_CIRCLE_COLOR} />
                {pointer}
                <circle cx="40" cy="40" r={innerRadius / 2} fill={INNER_CIRCLE_COLOR} stroke={POINTER_COLOR} strokeWidth="1" />
                <text x="40" y="44" textAnchor="middle" fontSize="10" fontWeight="bold" fill={POINTER_COLOR}>{score}</text>
            </svg>
        );
    }

    getSegments(segmentData, score, radius) {
        const total = segmentData.map(x => x.value).reduce((a, b) => a + b, 0);

        const lineWidth = 3;
        const startAngle = 150;
        const endAngle = 390;
        const angleRange = endAngle - startAngle;
        const gapAngle = 10;
        const totalGaps = (segmentData.length - 1) * gapAngle;
        const totalSegmentAngle = angleRange - totalGaps;
        let cumulativeValue = 0;

        let pointerAngle = startAngle; // Default value in case score is out of range

        const segments = segmentData.map((entry, index) => {
            const startValue = cumulativeValue;
            cumulativeValue += entry.value;
            const endValue = cumulativeValue;

            const segmentAngle = (endValue - startValue) / total * totalSegmentAngle;
            const largeArcFlag = segmentAngle > 180 ? 1 : 0;

            const startAngleRad = (startAngle + (startValue / total) * totalSegmentAngle + index * gapAngle) * (Math.PI / 180);
            const endAngleRad = (startAngle + (startValue / total) * totalSegmentAngle + segmentAngle + index * gapAngle) * (Math.PI / 180);

            const startX = 40 + radius * Math.cos(startAngleRad);
            const startY = 40 + radius * Math.sin(startAngleRad);
            const endX = 40 + radius * Math.cos(endAngleRad);
            const endY = 40 + radius * Math.sin(endAngleRad);

            // Calculate the center angle for the segment if it matches the score
            if (index === score - 1) {
                const centerValue = startValue + entry.value / 2;
                pointerAngle = startAngle + (centerValue / total) * totalSegmentAngle + index * gapAngle;
            }

            return (
                <path
                    key={index}
                    d={`M${startX},${startY} A${radius},${radius} 0 ${largeArcFlag},1 ${endX},${endY}`}
                    stroke={`url(#${this.getGradientId(index)})`}
                    strokeWidth={lineWidth}
                    strokeLinecap="round"
                    fill="none"
                />
            );
        });

        return { segments, pointerAngle };
    }

    getGradients(segmentData) {
        return segmentData.map((segment, index) => (
            <linearGradient id={this.getGradientId(index)} key={index} x1="0%" y1="0%" x2="100%" y2="100%" gradientUnits="userSpaceOnUse">
                <stop offset="0%" style={{ stopColor: segment.colorStart, stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: segment.colorEnd, stopOpacity: 1 }} />
            </linearGradient>
        ));
    }

    getGradientId = (index) => `tech-grad-${this.props.id}-${index}`;

    getPointer(pointerAngle, radius) {
        const pointerAngleRad = pointerAngle * (Math.PI / 180);
        const pointerLength = radius - 1;
        const pointerBaseWidth = 6;
        const pointerTipWidth = 1; // Width of the rounded tip

        const pointerTipX = 40 + pointerLength * Math.cos(pointerAngleRad);
        const pointerTipY = 40 + pointerLength * Math.sin(pointerAngleRad);

        const baseAngleRad1 = (pointerAngle - 90) * (Math.PI / 180);
        const baseAngleRad2 = (pointerAngle + 90) * (Math.PI / 180);

        const baseX1 = 40 + (pointerBaseWidth / 2) * Math.cos(baseAngleRad1);
        const baseY1 = 40 + (pointerBaseWidth / 2) * Math.sin(baseAngleRad1);
        const baseX2 = 40 + (pointerBaseWidth / 2) * Math.cos(baseAngleRad2);
        const baseY2 = 40 + (pointerBaseWidth / 2) * Math.sin(baseAngleRad2);

        const tipBaseX1 = pointerTipX + (pointerTipWidth / 2) * Math.cos(baseAngleRad1);
        const tipBaseY1 = pointerTipY + (pointerTipWidth / 2) * Math.sin(baseAngleRad1);
        const tipBaseX2 = pointerTipX + (pointerTipWidth / 2) * Math.cos(baseAngleRad2);
        const tipBaseY2 = pointerTipY + (pointerTipWidth / 2) * Math.sin(baseAngleRad2);

        return (
            <g>
                <polygon points={`40,40 ${baseX1},${baseY1} ${tipBaseX1},${tipBaseY1} ${tipBaseX2},${tipBaseY2} ${baseX2},${baseY2}`} fill={POINTER_COLOR} />
                <circle cx={pointerTipX} cy={pointerTipY} r={pointerTipWidth / 2} fill={POINTER_COLOR} />
            </g>
        );
    }
}

ESGTachometer.propTypes = {
    id: PropTypes.string.isRequired,
    data: PropTypes.shape({
        n_1: PropTypes.number,
        n_2: PropTypes.number,
        n_3: PropTypes.number,
        n_4: PropTypes.number,
        n_5: PropTypes.number,
    }).isRequired,
    score: PropTypes.number.isRequired,
};

ESGTachometer.defaultProps = {
    data: null,
};

export default withLocalize(ESGTachometer);